import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from 'styled-components';

import { Flags } from 'constants/flags';
import { useEpisode } from 'context/episode';
import { useSelectedLocationEpisode } from 'context/locationEpisode';
import { useProfile } from 'context/profile';
import { useEpisodeId } from 'hooks/useEpisodeId';
import { useSharedRecordTabsActions, useSharedRecordTabsStore } from 'stores/sharedRecordTabsStore';
import { colors } from 'styles/theme/colors';
import LinkIcon from 'svg/LinkIcon';

import { buildSharedRecordTabs, SharedRecordTab } from './buildSharedRecordTabs';

const LinkedRecordsBanner = () => {
  const { locationEpisode } = useSelectedLocationEpisode();
  const { episode } = useEpisode();
  const { profile } = useProfile();
  const navigate = useNavigate();
  const episodeId = useEpisodeId();

  const cachedTabs = useSharedRecordTabsStore((s) => s.tabs);
  const cachedActiveId = useSharedRecordTabsStore((s) => s.activeLocationEpisodeId);
  const { setTabs, clear } = useSharedRecordTabsActions();

  // Immediately discard cached tabs if the current episode isn't in them
  const cachedTabsRelevant = cachedTabs.length > 0 && cachedTabs.some((t) => t.episodeId === episodeId);
  const relevantCachedTabs = cachedTabsRelevant ? cachedTabs : [];

  useEffect(() => {
    if (!cachedTabsRelevant && cachedTabs.length > 0) clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cachedTabsRelevant]);

  const freshTabs = useMemo(() => {
    if (!locationEpisode || !episode) return [];
    if (!profile.hasFlag(Flags.SharedRecords)) return [];
    if (profile.canActAsManager && !profile.canActAsProvider) return [];
    if (locationEpisode.sharedRecords.length === 0) return [];
    if (!locationEpisode.latest || locationEpisode.archived) return [];

    return buildSharedRecordTabs(locationEpisode, episode.id);
  }, [locationEpisode, episode, profile]);

  useEffect(() => {
    if (freshTabs.length === 0 || relevantCachedTabs.length === 0) return;

    const cachedIdInFreshTabs = freshTabs.some((t) => t.locationEpisodeId === cachedActiveId);
    if (cachedIdInFreshTabs) {
      setTabs(freshTabs, cachedActiveId);
    } else {
      clear();
    }
    // Only run when fresh data arrives
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freshTabs]);

  const displayTabs: SharedRecordTab[] = freshTabs.length > 0 ? freshTabs : relevantCachedTabs;
  const activeLocationEpisodeId = locationEpisode?.id ?? cachedActiveId;

  if (displayTabs.length === 0) return null;

  const handleTabClick = (tab: SharedRecordTab) => {
    setTabs(displayTabs, tab.locationEpisodeId);
    navigate(`/patients/${tab.episodeId}?locationEpisodeId=${tab.locationEpisodeId}`);
  };

  const linkedCount = displayTabs.length;

  return (
    <Banner data-cy='linked-records-banner'>
      <TabsRow>
        {displayTabs.map((tab) => (
          <TabButton
            key={tab.key}
            $active={tab.locationEpisodeId === activeLocationEpisodeId}
            onClick={() => handleTabClick(tab)}>
            <TabLabel>{tab.label}</TabLabel>
            {tab.subtitle && <TabSubtitle>{tab.subtitle}</TabSubtitle>}
          </TabButton>
        ))}
      </TabsRow>
      <TabsFill>
        <LinkedInfo>
          <LinkIcon width={14} height={14} color={colors.black75} />
          <LinkedText>
            {linkedCount} shared patient {linkedCount === 1 ? 'record' : 'records'}
          </LinkedText>
        </LinkedInfo>
      </TabsFill>
    </Banner>
  );
};

export default LinkedRecordsBanner;

const Banner = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 0;
  background-color: ${colors.black05};
`;

const TabsRow = styled.div`
  display: flex;
`;

const TabButton = styled.button<{ $active: boolean }>`
  border: none;
  border-right: 1px solid ${colors.black10};
  border-bottom: ${({ $active }) => ($active ? 'none' : `1px solid ${colors.black10}`)};
  cursor: pointer;
  padding: 12px 24px;
  border-radius: 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: ${({ $active }) => ($active ? '#fff' : 'transparent')};
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${({ $active }) => ($active ? '#fff' : colors.black10)};
  }
`;

const TabsFill = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  align-self: stretch;
  border-bottom: 1px solid ${colors.black10};
`;

const TabLabel = styled.div`
  font-size: 12px;
  color: ${colors.black};
`;

const TabSubtitle = styled.div`
  font-size: 10px;
  color: ${colors.black};
`;

const LinkedInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 24px;

  @media ${({ theme }) => theme.devices.desktop} {
    padding: 0 56px;
  }
`;

const LinkedText = styled.span`
  font-size: 10px;
  color: ${colors.black75};
`;
